import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CursorProvider } from './CursorContext';
import { ToastContainer, toast } from 'react-toastify';
import SidePanel from '@CommonFunctionalDisplay/SidePanel';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import useThemeChange from '@Hooks/useThemeChange';
import ErrorReportService from '@Helpers/URLHelpers/ErrorReportService';
import useCheckPermission from '@Hooks/useCheckPermission';
import useSessionCheck from '@Hooks/useSessionCheck';
import { useReduxToken } from '@Hooks/reduxTokenHook/useReduxToken';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import SplashScreen from '@LandingPages/SplashScreen';
import { Grid, Box, useMediaQuery, useTheme } from '@mui/material';
import './AuthLayout.scss';

const AuthLayout = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const { setToken, token } = useReduxToken();
    const { currentProject } = useReduxProject();
    const { currentLeads } = useReduxLeads();
    const errorReportService = new ErrorReportService();
    const urlEncryptionService = new UrlEncryptionService();
    const userAuthService = new UserAuthService();
    const theme = useTheme();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const className = 'authLayout';

    const [showSplash, setShowSplash] = useState(true);
    const [fadeSplash, setFadeSplash] = useState(false);

    useThemeChange();
    useSessionCheck();

    useEffect(()=> {
        if(!token || token === 'void') {
            navigate('/logout');
        }
        if(currentProject == null || !currentProject) {
            navigate('/onboarding');
        }
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => setFadeSplash(true), 1000);
        const fadeTimer = setTimeout(() => setShowSplash(false), 1200);
        return () => {
            clearTimeout(timer);
            clearTimeout(fadeTimer);
        };
    }, []);

    window.addEventListener('error', e => {
        if (e.message === 'ResizeObserver loop limit exceeded') {
            const resizeObserverErrDiv = document.getElementById(
                'webpack-dev-server-client-overlay-div'
            );
            const resizeObserverErr = document.getElementById(
                'webpack-dev-server-client-overlay'
            );
            if (resizeObserverErr) {
                resizeObserverErr.setAttribute('style', 'display: none');
            }
            if (resizeObserverErrDiv) {
                resizeObserverErrDiv.setAttribute('style', 'display: none');
            }
        }
    });

    window.addEventListener('error', e => {
        if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
            const resizeObserverErrDiv = document.getElementById(
                'webpack-dev-server-client-overlay-div'
            );
            const resizeObserverErr = document.getElementById(
                'webpack-dev-server-client-overlay'
            );
            if (resizeObserverErr) {
                resizeObserverErr.setAttribute('style', 'display: none');
            }
            if (resizeObserverErrDiv) {
                resizeObserverErrDiv.setAttribute('style', 'display: none');
            }
        }
    });

    if(!token || token === 'void') {
        if(token === 'void') {
            setToken({'token': null});
            return <Navigate to='/login' state={{ from: location }} replace />
        }
        return (
            <>
                <ToastContainer
                    limit={1}
                    containerId={'login'}
                    position='top-center'
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover
                    theme='light'/>
                    <Navigate to='/login' state={{ from: location }} replace />
            </>
        );
    }

    return (
        <>
            {/* Global Splash Screen */}
            {showSplash && <SplashScreen fadeOut={fadeSplash} />}

                <ToastContainer
                    limit={1}
                    containerId={'error'}
                    position='bottom-left'
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover
                    theme='light'/>
                
                <CursorProvider>
                    <TopPanel/>
                    <div className={className}>
                        <SidePanel
                            isOpen={isSidebarOpen}
                            setIsOpen={setIsSidebarOpen} />
                        <div className={className + '__main'}>
                            <Outlet />
                        </div>
                    </div>
                </CursorProvider>

        </>
    );
};

export default AuthLayout;
